import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Package, Calendar, User, ShoppingCart, TrendingUp, DollarSign, Store, Globe, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { ReportsService, type SoldProduct, type ReportFilters } from '../../services/reports/reports.service';

interface ProductSaleHistoryDrawerProps {
  isOpen: boolean;
  product: SoldProduct | null;
  filters: ReportFilters;
  onClose: () => void;
}

const formatCurrency = (amount: number) => `$${amount.toLocaleString('es-CO')}`;

export const ProductSaleHistoryDrawer: React.FC<ProductSaleHistoryDrawerProps> = ({
  isOpen,
  product,
  filters,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 15;

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setPage(1);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  const { data: response, isLoading } = useQuery({
    queryKey: ['product-sale-history', product?.id, filters.fechaInicio, filters.fechaFin, filters.vendedorId, filters.proveedorId, page, limit],
    queryFn: async () => {
      if (!product) return { data: [], meta: { total: 0, page: 1, limit, totalPages: 0, hasNextPage: false, hasPreviousPage: false } };
      const res = await ReportsService.getProductSaleHistory(product.id, filters, page, limit);
      return res.data;
    },
    enabled: isOpen && !!product,
  });

  const items = response?.data || [];
  const meta = response?.meta || { total: 0, page: 1, limit, totalPages: 0, hasNextPage: false, hasPreviousPage: false };

  if (!isOpen || !product) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        } bg-black/30 backdrop-blur-sm`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-20 h-[calc(100vh-5rem)] w-full max-w-3xl bg-gray-50 shadow-2xl z-40 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 line-clamp-1">
                  {product.nombre}
                </h2>
                <p className="text-sm text-gray-500">
                  Historial de ventas · {meta.total} registros
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <ShoppingCart className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">No hay ventas registradas para este producto en el período seleccionado.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            item.tipo === 'POS'
                              ? 'bg-blue-50 text-blue-700 border border-blue-100'
                              : 'bg-purple-50 text-purple-700 border border-purple-100'
                          }`}
                        >
                          {item.tipo === 'POS' ? <Store className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                          {item.tipo === 'POS' ? 'Local' : 'Online'}
                        </span>
                        <span className="text-xs text-gray-400 font-mono">#{item.numeroPedido}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{formatCurrency(item.subtotal)}</span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          {new Date(item.fecha).toLocaleDateString('es-CO', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-700">{item.vendedor || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <TrendingUp className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-700">{item.cantidad} unid.</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <DollarSign className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-700">{formatCurrency(item.precioUnitario)} c/u</span>
                      </div>
                    </div>

                    {item.cliente && (
                      <p className="mt-2 text-xs text-gray-400">
                        Cliente: {item.cliente}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer / Pagination */}
          {meta.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Página {meta.page} de {meta.totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!meta.hasPreviousPage}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!meta.hasNextPage}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
